import React, { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import SettingsCard from '../../../components/Settings/SettingsCard'
import useApi from '../../../hooks/useApi'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import { buildQuotePayload, readQuote, QUOTE_FIELD } from './quoteFields'

/**
 * Inspirational quote card + editor modal.
 *
 * See quoteFields.js for the confirmed backend field contract.
 */
export default function QuoteCard({ user, setUser }) {
  const { t } = useTranslation()
  const { updateUserQuote } = useApi()
  const { setCurrentUser } = useCurrentUser()

  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [draftQuote, setDraftQuote] = useState('')

  const quote = readQuote(user)

  // Seed the draft when the modal opens. Keyed on `open` alone so a parent
  // re-render can't discard what's being typed.
  useEffect(() => {
    if (open) setDraftQuote(quote)
  }, [open]) // eslint-disable-line

  const onSave = async () => {
    if (!user?.id) return
    setIsSaving(true)
    try {
      const payload = buildQuotePayload(draftQuote.trim())
      const response = await updateUserQuote({ id: user.id, user: payload })

      if (response?.errors) {
        // eslint-disable-next-line no-alert
        alert(response.errors?.full_messages?.[0])
        return
      }

      // Patch only the quote field so unsaved edits in the other cards
      // survive. Prefer the server's echo of the field when it returns one.
      const savedQuote =
        response?.data?.[QUOTE_FIELD] !== undefined
          ? response.data[QUOTE_FIELD]
          : payload[QUOTE_FIELD]
      setUser(prev => ({ ...prev, [QUOTE_FIELD]: savedQuote }))
      setCurrentUser(prev => ({ ...prev, [QUOTE_FIELD]: savedQuote }))
      setOpen(false)
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(
        err?.response?.data?.errors?.full_messages?.[0] ??
          t('users.edit.quote.save_failed', 'Could not save quote'),
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <SettingsCard
      icon="quote-left"
      title={t('users.edit.quote.title', 'Inspirational Quote')}
      description={t(
        'users.edit.quote.message',
        'Share a quote that inspires you.',
      )}
    >
      {quote ? (
        <blockquote
          className="mb-3"
          style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}
        >
          {quote}
        </blockquote>
      ) : null}

      <Button variant="primary" onClick={() => setOpen(true)}>
        {quote
          ? t('users.edit.quote.edit', 'Edit Quote')
          : t('users.edit.quote.add', 'Add Quote')}
      </Button>

      <Modal show={open} onHide={() => setOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('users.edit.quote.title', 'Inspirational Quote')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold" htmlFor="quoteText">
              {t('users.edit.quote.label', 'Quote')}
            </label>
            <textarea
              id="quoteText"
              className="form-control"
              rows={5}
              maxLength={500}
              value={draftQuote}
              onChange={e => setDraftQuote(e.target.value)}
              placeholder={t(
                'users.edit.quote.placeholder',
                'Share a quote that inspires you.',
              )}
            />
            <small className="form-text text-muted">
              {draftQuote.length}/500
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            onClick={() => setOpen(false)}
            disabled={isSaving}
          >
            {t('settings.selection.cancel', 'Cancel')}
          </Button>
          <Button variant="primary" onClick={onSave} disabled={isSaving}>
            {isSaving ? t('users.edit.saving') : t('users.edit.save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </SettingsCard>
  )
}

QuoteCard.propTypes = {
  user: PropTypes.object,
  setUser: PropTypes.func.isRequired,
}
